import {
  CustomMessageTypes,
  MessageTypes,
} from '@shared/models/enums/message-types'
import { AttachmentContentTypes } from '@shared/models/enums/quick-forms'
import {
  isAudio as isAudioFile,
  isDocx,
  isImage as isImageFile,
  isPdf as isPdfFile,
  isVideo as isVideoFile,
} from '@shared/utils/helpers/attachments/file-types-predicates'

import { MessageType } from '@protos/chat/message_type_pb'

const isDocument = (contentType?: string) => {
  return {
    isShow: isDocx(contentType),
    key: CustomMessageTypes.DOCUMENT,
  }
}
const isImage = (contentType?: string, url?: string) => {
  return {
    isShow: isImageFile(contentType, url),
    key: MessageType.IMAGE,
  }
}
const isAudio = (contentType?: string, url?: string) => {
  return {
    isShow: isAudioFile(contentType, url),
    key: CustomMessageTypes.AUDIO,
  }
}
const isVideo = (contentType?: string, url?: string) => {
  return {
    isShow: isVideoFile(contentType, url),
    key: MessageType.VIDEO,
  }
}
const isPdf = (contentType?: string, url?: string) => {
  return {
    isShow: isPdfFile(contentType, url),
    key: MessageType.PDF,
  }
}
const isFaq = (contentType?: string) => ({
  isShow: contentType === AttachmentContentTypes.Faq,
  key: MessageType.FAQ,
})

const isSelfScheduler = (contentType?: string) => ({
  isShow:
    contentType === AttachmentContentTypes.SelfScheduler ||
    contentType === AttachmentContentTypes.OldSelfScheduler,
  key: MessageType.SCHEDULER,
})

const isQuickForm = (contentType?: string) => ({
  isShow:
    contentType === AttachmentContentTypes.QuickForm ||
    contentType === AttachmentContentTypes.QuickFormSubmission,
  key: MessageType.QUICK_FORM,
})

const isQuickSign = (contentType?: string) => ({
  isShow: contentType === AttachmentContentTypes.QuickSignDocument,
  key: MessageType.QUICK_SIGN,
})

const isTestEquipment = (contentType?: string) => ({
  isShow: contentType === AttachmentContentTypes.QuickTestResults,
  key: MessageType.AUDIO_VIDEO_TEST,
})

export const getAttachmentType = (contentType?: string, url?: string) => {
  return {
    docx: isDocument(contentType),
    image: isImage(contentType, url),
    audio: isAudio(contentType, url),
    video: isVideo(contentType, url),
    pdf: isPdf(contentType, url),
    faq: isFaq(contentType),
    scheduler: isSelfScheduler(contentType),
    quickSign: isQuickSign(contentType),
    quickForm: isQuickForm(contentType),
    testEquipment: isTestEquipment(contentType),
  }
}

export const getMessageTypeByContentType = (
  contentType?: string,
  url?: string
) => {
  return [
    isDocument,
    isImage,
    isAudio,
    isVideo,
    isPdf,
    isFaq,
    isSelfScheduler,
    isQuickSign,
    isQuickForm,
  ]
    .map((fn) => fn(contentType, url))
    .find((result) => result.isShow)?.key
}

export const getAttachmentTypeByMessageType = (type: MessageTypes) => {
  return {
    isDocx: type === CustomMessageTypes.DOCUMENT,
    isImage: type === MessageType.IMAGE,
    isAudio: type === CustomMessageTypes.AUDIO,
    isVideo: type === MessageType.VIDEO,
    isPdf: type === MessageType.PDF,
    isQuickSign: type === MessageType.QUICK_SIGN,
    isQuickForm: type === MessageType.QUICK_FORM,
    isCarousel: type === MessageType.CAROUSEL,
    isFaq: type === MessageType.FAQ,
    isSelfScheduler: type === MessageType.SCHEDULER,
  }
}

export const getAttachmentTypeByName = (name: string): string | null => {
  if (!name) {
    return null
  }

  const parts = name.split('.')
  if (parts.length < 2) {
    return null
  } // No extension found

  const res = parts?.pop()
  return res && res?.length < 7 ? res?.toLowerCase() : null // Get the last part and convert it to lowercase
}
